// Send to memory… (brief 49): who may send, and the send. A Manager or an Admin signed in on an
// organisation's host sends to its memory; in the desktop app, to the memory of an organisation
// the app is signed in to as one. The request is the Memories screen's Import TMX…,
// `memory.importTmx` on `/api/memory/requests`, with the bytes Export TMX writes.

import { invoke } from '@tauri-apps/api/core'
import { sha256 } from '../../core/pair.ts'
import { fetchFrame, responseOf } from '../../engine/native.ts'
import { Api } from '../../server/api.ts'
import type { Imported, Organisation } from '../core/memory.ts'
import { desktop } from './desktop.ts'
import { api, placeOf } from './review.ts'

/// The organisations whose memory the person may send to, none for everyone else.
export async function memoryOrganisations(): Promise<Organisation[]> {
  try {
    if (desktop) {
      const answer = await desktop.account({ kind: 'accounts' })
      return answer.kind === 'accounts' ? answer.accounts.filter(a => a.member.role !== 'user').map(a => ({ origin: a.origin, name: a.name })) : []
    }
    const place = await placeOf()
    if (place.kind !== 'member' || place.role === 'user') return []
    return [{ origin: '', name: (await api().about()).name }]
  } catch {
    return []
  }
}

/// The organisation's server: this origin's, or in the desktop app the runtime's call to the one
/// named, with its bearer.
function serverOf(organisation: Organisation): Api {
  if (!desktop) return api()
  const device = (window as { __INTERPLAE_DESKTOP__?: { device?: string } }).__INTERPLAE_DESKTOP__?.device ?? ''
  return new Api('', device, async (input, init) => responseOf(await invoke<ArrayBuffer>('fetch', fetchFrame(input, init, { origin: organisation.origin }))))
}

/// The TMX into the organisation's memory: what it took, and the new source's key, which is the
/// memory's for an imported file, `tmx:` and the SHA-256 of its bytes.
export async function sendToMemory(organisation: Organisation, name: string, xml: string): Promise<{ imported: Imported; source: string }> {
  const bytes = new TextEncoder().encode(xml).buffer as ArrayBuffer
  const source = `tmx:${await sha256(bytes)}`
  const imported = (await serverOf(organisation).organisation('memory', { type: 'memory.importTmx', name, bytes: bytes.slice(0) })) as Imported
  return { imported, source }
}

/// Memories on the new source: the shell beside this page, or the desktop app's main window.
export function openMemories(source: string): void {
  if (desktop) void desktop.tell({ kind: 'memories', source })
  else window.open(`/?memories=${encodeURIComponent(source)}`, '_blank', 'noopener')
}
